import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, Save, BadgeCheck, ShieldCheck, Lock, Loader2, AlertTriangle,
    Siren, ArrowUp, ArrowDown, Building2, Clock, Stethoscope, History, Pencil,
    CheckCircle2, Info,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { formatTime } from '../../utils/clock';
import {
    getOrderResults, enterResults, verifyResults, amendResult,
} from '../../redux/labSlice';

const MySwal = withReactContent(Swal);

// THE BENCH. One order, every analyte on it, and the two decisions that turn a
// set of numbers into something a clinician may act on.
//
// THE TWO-PERSON RULE IS THE ENTIRE SHAPE OF THIS SCREEN.
// Entering a value RECORDS it. It publishes nothing, it releases nothing, and it
// is not an opinion — it is a measurement being written down. Verifying is a
// different person making a different claim: that the number is right, that the
// control ran, that the sample was what it said it was. Those two acts are
// separated here by a visible wall — different pane, different colour, different
// weight, and a confirmation the verifier has to answer — because a "Verify"
// button that sits next to "Save" and looks like it will eventually be pressed
// by the same finger. The state machine the backend enforces is
//     pending -> entered -> verified -> released
// with 'amended' only ever following 'released', as a NEW row. Nothing here can
// skip a step, and this file draws no control that would try.
//
// WHY VERIFY IS REFUSED WHILE ANYTHING IS UNSAVED.
// Verification approves what is STORED, not what is on the screen. A technician
// who types 14.2, then presses Verify, would otherwise be approving the 9.6 that
// is still in the database — and the report would carry a signature against a
// value nobody looked at. So an unsaved edit disables the verify pane and says
// why, in those words.
//
// WHY VERIFY LOOKS DIFFERENT FROM SAVE.
// Save is teal-outlined and quiet: it is done twenty times an hour. Verify is a
// dark, bordered pane carrying a shield, a count, the verifier's own name and
// registration number, and a confirmation dialog. The visual weight is not
// decoration — it is the difference between a keystroke and a signature, and a
// bench under pressure reads weight faster than it reads labels.
//
// WHY A VERIFIER WITHOUT A REGISTRATION NUMBER IS REFUSED.
// An unsigned pathology report is not a report. The backend refuses with
// VERIFIER_NOT_REGISTERED when the verifier carries no Doctor.licenseNumber or
// Staff.registrationNo, and this screen says so in a sentence a person can act
// on ("your staff record has no registration number — an administrator must add
// it") rather than showing a raw error code.
//
// WHY VERIFYING YOUR OWN ENTRY IS A WARNING AND NOT A REFUSAL.
// A single-technician laboratory legitimately exists — most private clinics in
// the country are one. Blocking it would mean the software refuses to work in
// the commonest case, and the predictable outcome is two people sharing one
// login, which destroys the audit trail the rule was protecting. So it proceeds,
// and the fact is stated before it happens and recorded on the report audit.
//
// LIVE FLAGGING IS A PREVIEW AND THE SERVER IS ALWAYS RIGHT.
// flagPreview() below mirrors services/lab/labService.flagFor line for line —
// criticals first, strict inequalities so a value sitting exactly on a bound is
// inside it, a null bound meaning unbounded rather than zero, and a band with no
// reference interval never permitted to say 'normal'. It exists so the colour
// changes under the technician's fingers, which is what makes a mistyped decimal
// point visible before it is saved. What is stored and printed is always what
// the server computed; if the two ever disagree, the server is right and this
// file is the bug.
//
// WHERE THE REFERENCE RANGE COMES FROM, AND WHAT AN EMPTY ONE MEANS.
// The range is resolved server-side against the test, the patient's gender and
// their AGE IN DAYS AT COLLECTION — days, so paediatric bands work at all. When
// no band matches, the columns stay BLANK and the flag stays empty. That is the
// honest answer and it is not the same as 'normal': printing "normal" under a
// number that was never compared against anything is the software reassuring a
// patient on its own authority.
//
// A CRITICAL VALUE IS A TELEPHONE CALL, not a colour. The banner says so, and
// the backend raises a lab_critical_alerts row on entry so NABL has a record of
// who was told and when.
//
// ---------------------------------------------------------------------------
// THE API THIS SCREEN CONSUMES lives in redux/labSlice.js — getOrderResults,
// enterResults, verifyResults, amendResult — and no URL is written in this file.
// One caller per endpoint is what stops two screens drifting into two spellings
// of the same path, which is precisely the defect this module was repaired for.
//
// getOrderResults answers with the whole bench in one block:
//   order:   { orderId, priority, orderedAt, doctorName, status }
//   patient: { patientId, name, age, ageDays, gender, dob }
//   sample:  { sampleId, accessionNo, sampleType, status, collectedAt,
//              receivedAt }
//   viewer:  { userId, name, registrationNo, canVerify }
//   results: [result]
//   alerts:  [{ alertId, resultId, testName, resultValue, informedTo,
//              informedAt, acknowledged }]
//
// A result row, with every field this file reads:
//   resultId, orderId, orderItemId, sampleId, testId, testName, panelTestId,
//   panelName, sectionName, sortOrder, unit, resultValue, numericValue, flag,
//   refLow, refHigh, refText, method, status, enteredBy, enteredByName,
//   enteredAt, verifiedBy, verifiedByName, verifiedAt, releasedAt,
//   amendedFromResultId, amendReason, isOutsourced, partnerName, remarks,
//   resultType, options[], decimals, textNormal,
//   range: { low, high, criticalLow, criticalHigh, unit, textNormal, note }
//
// `range` carries the CRITICAL bounds, which the result row itself does not
// snapshot (labResultModel keeps refLow/refHigh only). Without it the screen
// cannot preview a panic value and says so rather than colouring a lethal
// potassium the same green as a normal one — see criticalPreviewAvailable.
// ---------------------------------------------------------------------------

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Mirror of labService.toNumOrNull. '' and '<0.01' are not numbers, and 0 is. */
const toNumOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
};

/** Mirror of labService.sameText. Case and collapsed whitespace are not clinical
 *  content: 'Not Detected' and 'not detected' are the same finding, and treating
 *  them as different raises an abnormal flag on an HIV screen because somebody
 *  had caps lock on. */
const sameText = (a, b) =>
    String(a).trim().replace(/\s+/g, ' ').toLowerCase() ===
    String(b).trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Mirror of services/lab/labService.flagFor. A PREVIEW ONLY — see the header.
 *
 * Returns 'critical_low' | 'critical_high' | 'low' | 'high' | 'normal' |
 * 'abnormal' | ''. The empty string is a real answer and is not 'normal': it
 * means no opinion is being offered, because nothing comparable was measured or
 * the lab holds no reference for this patient.
 */
function flagPreview({ resultType = 'numeric', resultValue = null, range = null, textNormal = null } = {}) {
    const type = String(resultType || 'numeric').toLowerCase();

    if (type === 'numeric') {
        const n = toNumOrNull(resultValue);
        // '<0.01', 'Sample haemolysed', or nothing typed yet. Not an error and
        // not normal.
        if (n === null) return '';
        if (!range) return '';

        const low = toNumOrNull(range.low);
        const high = toNumOrNull(range.high);
        const criticalLow = toNumOrNull(range.criticalLow);
        const criticalHigh = toNumOrNull(range.criticalHigh);

        // A null bound is unbounded at that end, never zero. CRP is reported as
        // < 5 with no meaningful lower limit; substituting 0 for a missing high
        // would flag every result in the hospital.
        if (criticalLow !== null && n < criticalLow) return 'critical_low';
        if (criticalHigh !== null && n > criticalHigh) return 'critical_high';
        if (low !== null && n < low) return 'low';
        if (high !== null && n > high) return 'high';

        // Only a band with a real reference interval may call a value normal.
        if (low === null && high === null) return '';
        return 'normal';
    }

    const reported = String(resultValue ?? '').trim();
    if (!reported) return '';
    const expected = textNormal && String(textNormal).trim()
        ? String(textNormal)
        : range?.textNormal ? String(range.textNormal) : '';
    if (!expected.trim()) return '';
    return sameText(reported, expected) ? 'normal' : 'abnormal';
}

// Colour, an arrow, and a word. Three codings, for the same reason the worklist
// codes priority three ways: colour alone fails one man in twelve and fails
// every elderly monitor in every laboratory in the country.
const FLAG_STYLES = {
    critical_low: { cls: 'bg-red-600 text-white border-red-700', icon: Siren, fallback: 'CRITICAL LOW' },
    critical_high: { cls: 'bg-red-600 text-white border-red-700', icon: Siren, fallback: 'CRITICAL HIGH' },
    low: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: ArrowDown, fallback: 'LOW' },
    high: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: ArrowUp, fallback: 'HIGH' },
    abnormal: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle, fallback: 'ABNORMAL' },
    normal: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, fallback: 'NORMAL' },
};

const isCritical = (flag) => flag === 'critical_low' || flag === 'critical_high';

// The border that makes a wrong value visible from across the bench without
// having to read the flag chip.
const INPUT_TONE = {
    critical_low: 'border-red-500 ring-2 ring-red-200 bg-red-50 text-red-900 font-bold',
    critical_high: 'border-red-500 ring-2 ring-red-200 bg-red-50 text-red-900 font-bold',
    low: 'border-amber-400 bg-amber-50 text-amber-900 font-semibold',
    high: 'border-amber-400 bg-amber-50 text-amber-900 font-semibold',
    abnormal: 'border-amber-400 bg-amber-50 text-amber-900 font-semibold',
    normal: 'border-emerald-300 bg-emerald-50/40 text-slate-800',
};

const POSITIVE_NEGATIVE = ['Positive', 'Negative'];

/**
 * Escape a value being interpolated into a sweetalert2 `html` body.
 *
 * The confirmation dialogs below need more than one sentence and a list, which
 * `text` cannot lay out, so they build markup. Test names, staff names and
 * reported values are all tenant-authored master data — an `<img onerror=...>`
 * typed into a test name would otherwise execute in the verifier's session at
 * the exact moment they are about to sign a clinical document. React escapes
 * everything it renders; this is the one place on the screen that bypasses it,
 * so it does the job by hand.
 */
const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ESCAPES[c]);

/** Statuses whose value may still be typed into. 'verified' is not one of them:
 *  correcting a verified value is an amendment, which creates a new row. */
const EDITABLE_STATUSES = ['pending', 'entered'];

const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    // The time part is the one clock format ("2:30 PM", utils/clock).
    return Number.isNaN(d.getTime())
        ? ''
        : `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })}, ${formatTime(d)}`;
};

/* ------------------------------------------------------------------ *
 * Presentational pieces
 *
 * Outside the component: none of them reads page state, and rebuilding them on
 * every keystroke is a cost that lands exactly where it is felt — a bench typing
 * thirty values into one screen.
 * ------------------------------------------------------------------ */

/** The flag. Empty renders NOTHING, on purpose — see flagPreview. */
function FlagChip({ flag, preview, testId }) {
    const { t } = useTranslation();
    const style = FLAG_STYLES[flag];
    if (!style) return null;
    const Icon = style.icon;
    return (
        <span
            data-testid={testId}
            title={preview ? t('labresultentry.flag_preview_hint', 'Provisional — the flag is decided by the server when you save') : undefined}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${style.cls} ${preview ? 'opacity-90 border-dashed' : ''}`}
        >
            <Icon className="w-3 h-3" />
            {t(`labresultentry.flag_${flag}`, style.fallback)}
        </span>
    );
}

/**
 * The reference interval, beside the input rather than in a tooltip.
 *
 * A technician reading 14.2 has to see 12.0–15.0 in the same glance or the
 * number means nothing; a range that is one hover away is a range nobody looks
 * at. When the lab holds no band for this patient the cell says so in words —
 * silence there would read as "inside the range".
 */
const present = (v) => v !== null && v !== undefined && v !== '';

function ReferenceCell({ result }) {
    const { t } = useTranslation();
    const range = result.range || {};
    const low = result.refLow ?? range.low;
    const high = result.refHigh ?? range.high;
    const text = result.refText || range.textNormal || result.textNormal;
    const unit = result.unit || range.unit || '';

    // A one-sided band is a band. TSH is reported against 0.4–4.0, CRP against
    // "< 5" with no lower limit at all, and demanding both bounds before the
    // column renders would blank the reference on half the endocrine menu.
    const hasNumeric = present(low) || present(high);

    if (!hasNumeric && !text) {
        return (
            <span
                data-testid={`ref-none-${result.resultId}`}
                title={t('labresultentry.no_range_hint', 'No reference band is configured for this test at this patient’s age and sex, so nothing is flagged')}
                className="text-[11px] text-slate-400 italic"
            >
                {t('labresultentry.no_range', 'no reference on file')}
            </span>
        );
    }

    const criticalLow = range.criticalLow;
    const criticalHigh = range.criticalHigh;
    const hasCritical = present(criticalLow) || present(criticalHigh);

    return (
        <div data-testid={`ref-${result.resultId}`} className="leading-tight">
            {hasNumeric ? (
                <span className="font-mono text-sm text-slate-700 font-semibold">
                    {present(low) ? low : '—'}
                    {' – '}
                    {present(high) ? high : '—'}
                </span>
            ) : (
                <span className="text-sm text-slate-700 font-semibold">{text}</span>
            )}
            {unit && <span className="text-[11px] text-slate-400 ml-1">{unit}</span>}
            {hasNumeric && text && <div className="text-[11px] text-slate-400">{text}</div>}
            {hasCritical && (
                <div className="text-[10px] text-red-600 font-semibold mt-0.5">
                    {t('labresultentry.panic_at', 'panic < {{low}} or > {{high}}', {
                        low: present(criticalLow) ? criticalLow : '—',
                        high: present(criticalHigh) ? criticalHigh : '—',
                    })}
                </div>
            )}
        </div>
    );
}

/** Who has touched this line, and when. The audit trail, on the row it belongs
 *  to, rather than on a separate screen nobody opens. */
function ProvenanceCell({ result }) {
    const { t } = useTranslation();
    if (result.status === 'pending') {
        return <span className="text-[11px] text-slate-400">{t('labresultentry.awaiting_value', 'awaiting a value')}</span>;
    }
    return (
        <div className="text-[11px] text-slate-500 leading-relaxed">
            {result.enteredAt && (
                <div>
                    <span className="text-slate-400">{t('labresultentry.entered_by', 'Entered')}</span>{' '}
                    <span className="font-semibold text-slate-600">{result.enteredByName || '—'}</span>
                    <span className="text-slate-400">{` · ${fmtDateTime(result.enteredAt)}`}</span>
                </div>
            )}
            {result.verifiedAt && (
                <div className="text-[#2D8A8A]">
                    <span className="opacity-70">{t('labresultentry.verified_by', 'Verified')}</span>{' '}
                    <span className="font-semibold">{result.verifiedByName || '—'}</span>
                    <span className="opacity-70">{` · ${fmtDateTime(result.verifiedAt)}`}</span>
                </div>
            )}
            {result.releasedAt && (
                <div className="text-slate-400">
                    {t('labresultentry.released_at', 'Released {{when}}', { when: fmtDateTime(result.releasedAt) })}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function LabResultEntry() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { can } = usePermissions();
    const session = useSession();

    // Route param first, query string second. The worklist links with a path,
    // but a barcode scanner wired to open a URL, and every "email me the link"
    // habit in a hospital, produce ?orderId= — accepting both costs a line and
    // saves a support call.
    const orderId = params.orderId || searchParams.get('orderId') || '';

    // WHOSE LABORATORY. Ignored for the technician, who carries their hospital in
    // the verified token and cannot name another; required for Super Admin, who
    // carries none and is answered 400 until the organisation is named. Sent on
    // every call so this screen serves both without a second code path.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    const canEnter = can('lab.result.enter');
    const canVerifyPermission = can('lab.result.verify');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [patient, setPatient] = useState(null);
    const [sample, setSample] = useState(null);
    const [viewer, setViewer] = useState(null);
    const [results, setResults] = useState([]);
    const [alerts, setAlerts] = useState([]);

    // resultId -> the text in the box. Held apart from `results` so that the
    // stored value and the typed value are never the same variable: the verify
    // pane has to be able to tell them apart, and a poll or a save that merged
    // them would silently adopt whatever was half-typed.
    const [draft, setDraft] = useState({});
    const [remarksDraft, setRemarksDraft] = useState({});

    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    /* -------------------------------------------------------------- *
     * Load
     * -------------------------------------------------------------- */

    const load = useCallback(async () => {
        if (!orderId) {
            setError(t('labresultentry.no_order', 'No order was named. Open a row from the worklist.'));
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await dispatch(getOrderResults({ orderId, tenantId })).unwrap() || {};
            setOrder(data.order || null);
            setPatient(data.patient || null);
            setSample(data.sample || data.samples?.[0] || null);
            setViewer(data.viewer || null);
            setAlerts(data.alerts || []);

            const rows = data.results || [];
            setResults(rows);
            // The boxes are seeded from what is stored, so "unsaved" starts
            // empty and means exactly what it says.
            const seed = {};
            const seedRemarks = {};
            rows.forEach((r) => {
                seed[r.resultId] = r.resultValue ?? '';
                seedRemarks[r.resultId] = r.remarks ?? '';
            });
            setDraft(seed);
            setRemarksDraft(seedRemarks);
            setError(null);
        } catch (err) {
            setError(extractErrorMessage(err, t('labresultentry.load_error', 'Could not load this order')));
        } finally {
            setLoading(false);
        }
    }, [dispatch, orderId, tenantId, t]);

    useEffect(() => { load(); }, [load, reloadKey]);

    /* -------------------------------------------------------------- *
     * Derived state
     *
     * NOTE there is no polling on this screen, unlike the worklist. A bench that
     * is typing must not have the row under its cursor replaced by somebody
     * else's copy of it; the worklist refreshes because it is a list being read,
     * this is a form being filled in.
     * -------------------------------------------------------------- */

    const byId = useMemo(() => new Map(results.map((r) => [r.resultId, r])), [results]);

    /** Rows whose box no longer matches what is stored. */
    const dirtyIds = useMemo(() => results
        .filter((r) => EDITABLE_STATUSES.includes(r.status))
        .filter((r) => (draft[r.resultId] ?? '') !== (r.resultValue ?? '')
            || (remarksDraft[r.resultId] ?? '') !== (r.remarks ?? ''))
        .map((r) => r.resultId), [results, draft, remarksDraft]);

    const hasUnsaved = dirtyIds.length > 0;

    /** Entered-and-stored, therefore approvable. A row still being typed is not
     *  in this list — see the header on why unsaved edits block verification. */
    const verifiableIds = useMemo(
        () => results.filter((r) => r.status === 'entered').map((r) => r.resultId),
        [results]
    );

    /** The live flag for a row: previewed from the box while it differs from the
     *  stored value, otherwise the flag the server actually decided. */
    const liveFlag = useCallback((result) => {
        const typed = draft[result.resultId] ?? '';
        const stored = result.resultValue ?? '';
        if (typed === stored) return { flag: result.flag || '', preview: false };
        return {
            flag: flagPreview({
                resultType: result.resultType,
                resultValue: typed,
                range: result.range || (result.refLow != null || result.refHigh != null
                    ? { low: result.refLow, high: result.refHigh, textNormal: result.refText }
                    : null),
                textNormal: result.textNormal,
            }),
            preview: true,
        };
    }, [draft]);

    // Whether the screen is even ABLE to preview a panic value. The result row
    // snapshots refLow/refHigh but not the critical bounds, so without the
    // resolved `range` block the client would colour a lethal potassium the same
    // green as a normal one — which is worse than not colouring it at all. Said
    // out loud rather than silently degraded.
    const criticalPreviewAvailable = useMemo(
        () => results.some((r) => r.range && (r.range.criticalLow != null || r.range.criticalHigh != null)),
        [results]
    );

    // ONLY the rows being typed into. A critical that is already stored is a
    // different problem with a different fix — it needs a telephone call
    // recorded against it, which is the `unphoned` banner's job — and rolling
    // the two together would leave a saved, phoned-through potassium shouting
    // "check it against the analyser" for the rest of the shift.
    const liveCriticals = useMemo(
        () => results.filter((r) => {
            const { flag, preview } = liveFlag(r);
            return preview && isCritical(flag);
        }),
        [results, liveFlag]
    );

    /** Rows this user would be verifying that they themselves entered. */
    const selfEnteredCount = useMemo(() => {
        const me = viewer?.userId;
        if (!me) return 0;
        return verifiableIds.filter((id) => byId.get(id)?.enteredBy === me).length;
    }, [verifiableIds, byId, viewer]);

    /**
     * Why the verify pane is shut, in one word the render can switch on.
     * Ordered by what the person can do about it: a missing permission is
     * somebody else's job, an unsaved edit is theirs and takes one click.
     */
    const verifyBlockedBy = useMemo(() => {
        if (!canVerifyPermission) return 'permission';
        // The backend refuses the same case with VERIFIER_NOT_REGISTERED. Saying
        // it here, before anything is typed, is the difference between a warning
        // and a wasted shift.
        if (viewer && viewer.registrationNo !== undefined && !viewer.registrationNo) return 'registration';
        if (hasUnsaved) return 'unsaved';
        if (!verifiableIds.length) return 'nothing';
        return null;
    }, [canVerifyPermission, viewer, hasUnsaved, verifiableIds.length]);

    /* -------------------------------------------------------------- *
     * Grouping
     *
     * Section, then panel. A lipid profile prints as one block with its five
     * analytes under it because that is how it was ordered, how it is billed and
     * how the report reads — and because the panel ITSELF has no result row of
     * its own, by design: there is no such measurement as "a lipid profile".
     * -------------------------------------------------------------- */
    const groups = useMemo(() => {
        const out = [];
        const index = new Map();
        [...results]
            .sort((a, b) => toNum(a.sortOrder) - toNum(b.sortOrder))
            .forEach((r) => {
                const sectionKey = r.sectionName || t('labresultentry.other_section', 'Other');
                const panelKey = r.panelTestId || '';
                const key = `${sectionKey}::${panelKey}`;
                if (!index.has(key)) {
                    const group = {
                        key,
                        sectionName: sectionKey,
                        panelTestId: panelKey || null,
                        panelName: r.panelName || null,
                        rows: [],
                    };
                    index.set(key, group);
                    out.push(group);
                }
                index.get(key).rows.push(r);
            });
        return out;
    }, [results, t]);

    /* -------------------------------------------------------------- *
     * Save — the ENTER half
     * -------------------------------------------------------------- */

    const handleSave = async () => {
        if (!hasUnsaved || saving) return;
        setSaving(true);
        try {
            const payload = {
                orderId,
                tenantId,
                // numericValue is deliberately NOT sent. Turning '<0.01' or
                // '1,240' or 'Not detected' into a comparable number is one
                // decision and it belongs on the server, beside the flagging that
                // depends on it. Two implementations of that parse is how a
                // '<0.01' becomes 0.01 on the report.
                results: dirtyIds.map((id) => ({
                    resultId: id,
                    resultValue: String(draft[id] ?? '').trim(),
                    remarks: String(remarksDraft[id] ?? '').trim() || null,
                })),
            };
            const body = await dispatch(enterResults(payload)).unwrap();
            const raised = body?.data?.criticalAlerts || body?.criticalAlerts || [];

            toast.success(t('labresultentry.saved', '{{n}} value(s) recorded', { n: payload.results.length }));

            // A critical value is not a colour, it is a telephone call, and the
            // moment it is stored is the moment somebody has to make it. So the
            // confirmation is a dialog that has to be dismissed, not a toast
            // that slides away while the technician is looking at the analyser.
            if (raised.length) {
                await MySwal.fire({
                    title: t('labresultentry.critical_title', 'Critical value — telephone a clinician now'),
                    html: `<div style="text-align:left;font-size:14px;color:#334155">
                        <p style="margin-bottom:8px">${t('labresultentry.critical_body', 'These results are outside the panic limits. A clinician must be told by telephone before anybody waits for the report, and who you told has to be recorded.')}</p>
                        <ul style="margin:0;padding-left:18px">
                            ${raised.map((a) => `<li><strong>${esc(a.testName)}</strong>: ${esc(a.resultValue)}</li>`).join('')}
                        </ul>
                    </div>`,
                    icon: 'warning',
                    confirmButtonColor: '#dc2626',
                    confirmButtonText: t('labresultentry.critical_ack', 'Understood'),
                });
            }
            setReloadKey((k) => k + 1);
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('labresultentry.save_error', 'Could not record these values')));
        } finally {
            setSaving(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Verify — the second person
     * -------------------------------------------------------------- */

    const handleVerify = async () => {
        if (verifyBlockedBy || verifying || !verifiableIds.length) return;

        const criticalRows = verifiableIds
            .map((id) => byId.get(id))
            .filter((r) => r && isCritical(r.flag));

        const lines = [];
        lines.push(`<p style="margin-bottom:10px">${t(
            'labresultentry.verify_body',
            'You are putting your name to {{n}} result(s). Once verified they can be released as a signed report a clinician will act on.',
            { n: verifiableIds.length }
        )}</p>`);

        if (viewer?.name) {
            lines.push(`<p style="margin-bottom:10px;font-size:13px;color:#475569">
                ${t('labresultentry.verify_as', 'Signing as')} <strong>${esc(viewer.name)}</strong>${viewer.registrationNo ? ` · ${t('labresultentry.reg_no', 'Reg. no')} ${esc(viewer.registrationNo)}` : ''}
            </p>`);
        }

        if (criticalRows.length) {
            lines.push(`<p style="margin-bottom:10px;padding:8px 10px;border-radius:8px;background:#fef2f2;color:#991b1b;font-size:13px">
                ${t('labresultentry.verify_critical', '{{n}} of these is a critical value. Confirm a clinician has been telephoned before you verify.', { n: criticalRows.length })}
            </p>`);
        }

        // Stated plainly, not hidden. A one-technician lab is legitimate and this
        // does not refuse it — but the fact goes on the report audit, and the
        // person doing it should learn that from the screen and not from an
        // inspection.
        if (selfEnteredCount > 0) {
            lines.push(`<p style="padding:8px 10px;border-radius:8px;background:#fffbeb;color:#92400e;font-size:13px">
                ${t('labresultentry.verify_self', 'You entered {{n}} of these yourself. That is allowed, and it will be recorded on the report audit as a single-operator verification.', { n: selfEnteredCount })}
            </p>`);
        }

        const confirm = await MySwal.fire({
            title: t('labresultentry.verify_title', 'Verify these results?'),
            html: `<div style="text-align:left;font-size:14px;color:#334155">${lines.join('')}</div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2D8A8A',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('labresultentry.verify_confirm', 'Verify and sign'),
            cancelButtonText: t('labresultentry.cancel', 'Cancel'),
        });
        if (!confirm.isConfirmed) return;

        setVerifying(true);
        try {
            await dispatch(verifyResults({ orderId, resultIds: verifiableIds, tenantId })).unwrap();
            toast.success(t('labresultentry.verified', '{{n}} result(s) verified', { n: verifiableIds.length }));
            setReloadKey((k) => k + 1);
        } catch (err) {
            // `unwrap()` throws the slice's rejection payload, not the axios
            // error, so the backend's code sits at the top level. That payload is
            // the whole point of routing through the slice: this screen has to
            // tell VERIFIER_NOT_REGISTERED — which no technician can fix by
            // typing differently — from every other refusal.
            const code = err?.code;
            if (code === 'VERIFIER_NOT_REGISTERED') {
                // The one refusal on this screen that a technician cannot fix by
                // doing anything differently, so it is spelled out rather than
                // toasted.
                await MySwal.fire({
                    title: t('labresultentry.not_registered_title', 'No registration number on your record'),
                    text: t(
                        'labresultentry.not_registered_text',
                        'A pathology report carries the registration number of whoever signed it, so verification cannot proceed without one. An administrator needs to add it to your staff record.'
                    ),
                    icon: 'error',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('labresultentry.done', 'Done'),
                });
            } else {
                toast.warn(extractErrorMessage(err, t('labresultentry.verify_error', 'Could not verify these results')));
            }
        } finally {
            setVerifying(false);
        }
    };

    /* -------------------------------------------------------------- *
     * Amend — correcting something the patient already has
     * -------------------------------------------------------------- */

    const handleAmend = async (result) => {
        const confirm = await MySwal.fire({
            title: t('labresultentry.amend_title', 'Amend this result?'),
            html: `<div style="text-align:left;font-size:14px;color:#334155">
                <p style="margin-bottom:10px">${t(
                    'labresultentry.amend_body',
                    'This result has already been released and the patient may be holding a copy. Amending does not overwrite it — a new, corrected result is issued and the old one stays on the record beside it.'
                )}</p>
                <p style="font-size:13px;color:#475569">${t('labresultentry.amend_current', 'Currently reported as')} <strong>${esc(result.resultValue)}</strong> ${esc(result.unit)}</p>
            </div>`,
            icon: 'warning',
            input: 'text',
            inputLabel: t('labresultentry.amend_new_value', 'Corrected value'),
            inputValue: result.resultValue ?? '',
            inputAttributes: { maxlength: 120 },
            inputValidator: (value) => (
                !String(value || '').trim()
                    ? t('labresultentry.amend_value_required', 'A corrected value is required')
                    : undefined
            ),
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('labresultentry.amend_next', 'Next'),
            cancelButtonText: t('labresultentry.cancel', 'Cancel'),
        });
        if (!confirm.isConfirmed) return;

        // Two steps on purpose. The reason is the clinically important half — it
        // is what a reader of the amended report is told about why the number
        // changed — and bundling it under the value invites "correction" as the
        // answer to both.
        const why = await MySwal.fire({
            title: t('labresultentry.amend_reason_title', 'Why is it being amended?'),
            input: 'text',
            inputPlaceholder: t('labresultentry.amend_reason_placeholder', 'e.g. transcription error, sample re-run after dilution'),
            inputAttributes: { maxlength: 200 },
            inputValidator: (value) => (
                String(value || '').trim().length < 4
                    ? t('labresultentry.amend_reason_required', 'A reason is required — it is printed on the amended report')
                    : undefined
            ),
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('labresultentry.amend_confirm', 'Issue amendment'),
            cancelButtonText: t('labresultentry.cancel', 'Cancel'),
        });
        if (!why.isConfirmed) return;

        try {
            await dispatch(amendResult({
                resultId: result.resultId,
                resultValue: String(confirm.value).trim(),
                amendReason: String(why.value).trim(),
                tenantId,
            })).unwrap();
            toast.success(t('labresultentry.amended', 'Amendment issued — it needs verifying like any new result'));
            setReloadKey((k) => k + 1);
        } catch (err) {
            toast.warn(extractErrorMessage(err, t('labresultentry.amend_error', 'Could not amend this result')));
        }
    };

    /* -------------------------------------------------------------- *
     * One editable cell, by resultType
     *
     * The MASTER's declaration decides the control, never what the value happens
     * to look like. A numeric test gets a TEXT box and not <input type=number>,
     * because '<0.01', '>1000' and 'Sample haemolysed' are all real, reportable
     * results that a number input silently refuses to hold — and a technician who
     * cannot type what the analyser printed writes something else instead.
     * -------------------------------------------------------------- */
    const renderInput = (result) => {
        const editable = canEnter && EDITABLE_STATUSES.includes(result.status);
        const value = draft[result.resultId] ?? '';
        const { flag } = liveFlag(result);
        const tone = INPUT_TONE[flag] || 'border-gray-300 bg-white text-slate-800';

        if (!editable) {
            return (
                <div data-testid={`value-readonly-${result.resultId}`} className="font-mono text-sm font-bold text-slate-800">
                    {result.resultValue || '—'}
                    {result.unit && <span className="text-[11px] text-slate-400 font-sans font-normal ml-1">{result.unit}</span>}
                </div>
            );
        }

        // `common` is shared by the <select> and <input> branches below, so the
        // `tt-select` skin cannot be folded into it — the free-text result box
        // would grow a dropdown chevron it can do nothing with. The two selects
        // re-state className AFTER the spread instead, which is why they read
        // `{...common} className={...common.className}`.
        const common = {
            'data-testid': `value-${result.resultId}`,
            value,
            onChange: (e) => setDraft((d) => ({ ...d, [result.resultId]: e.target.value })),
            className: `w-full p-2 border-2 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] transition-colors ${tone}`,
        };

        const type = String(result.resultType || 'numeric').toLowerCase();

        if (type === 'options' && (result.options || []).length) {
            return (
                <select {...common} className={`tt-select ${common.className}`}>
                    <option value="">{t('labresultentry.choose', '—')}</option>
                    {result.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        }

        if (type === 'positive_negative') {
            return (
                <select {...common} className={`tt-select ${common.className}`}>
                    <option value="">{t('labresultentry.choose', '—')}</option>
                    {POSITIVE_NEGATIVE.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        }

        return (
            <input
                {...common}
                type="text"
                inputMode={type === 'numeric' ? 'decimal' : 'text'}
                maxLength={120}
                autoComplete="off"
                placeholder={type === 'numeric' ? t('labresultentry.value_placeholder', 'value') : t('labresultentry.text_placeholder', 'finding')}
            />
        );
    };

    /* -------------------------------------------------------------- *
     * Render
     * -------------------------------------------------------------- */

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-2xl shadow-md w-full">
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('labresultentry.loading', 'Loading...')}</span>
                </div>
            </div>
        );
    }

    if (error && !results.length) {
        return (
            <div data-testid="lab-result-entry-error" className="bg-white p-6 rounded-2xl shadow-md w-full">
                <button
                    type="button"
                    onClick={() => navigate('/lab/worklist')}
                    className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer mb-4"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('labresultentry.back', 'Back to the worklist')}
                </button>
                <div className="flex items-center gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            </div>
        );
    }

    const priority = String(order?.priority || 'routine').toLowerCase();
    const unphoned = alerts.filter((a) => !a.acknowledged && !a.informedAt);

    return (
        <div data-testid="lab-result-entry-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full pb-40">
            {/* ---- who and what ---- */}
            <div className="flex flex-col gap-4 mb-6">
                <button
                    type="button"
                    data-testid="back-to-worklist"
                    onClick={() => navigate('/lab/worklist')}
                    className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer w-fit"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('labresultentry.back', 'Back to the worklist')}
                </button>

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-blue-50/30 border border-gray-200">
                    <div className="flex items-start gap-4">
                        {/* The accession number, drawn exactly as the worklist
                            draws it — this is the string on the tube in the
                            technician's other hand. No accession is not '-': it
                            means no tube exists, which for an outsourced test is
                            normal and for anything else is the finding. */}
                        {sample?.accessionNo ? (
                            <span
                                data-testid="accession-no"
                                className="inline-flex items-center px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md font-mono text-xl leading-none font-black tracking-wider whitespace-nowrap"
                            >
                                {sample.accessionNo}
                            </span>
                        ) : (
                            <span
                                data-testid="accession-no"
                                title={t('labresultentry.not_drawn_hint', 'No sample has been accessioned against this order')}
                                className="inline-flex items-center px-3 py-2 rounded-xl bg-amber-100 border-2 border-dashed border-amber-400 text-amber-700 text-xs font-bold uppercase tracking-wide whitespace-nowrap"
                            >
                                {t('labresultentry.not_drawn', 'Not drawn')}
                            </span>
                        )}
                        <div>
                            <h1 data-testid="patient-name" className="text-xl font-bold text-slate-800">
                                {patient?.name || t('labresultentry.unknown_patient', 'Unknown patient')}
                            </h1>
                            <div className="text-xs text-slate-500 mt-0.5">
                                {[
                                    patient?.age,
                                    patient?.gender,
                                    hospitalPatientCode(patient),
                                ].filter(Boolean).join(' · ')}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                {priority === 'stat' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-600 text-white text-[11px] font-black uppercase tracking-widest">
                                        <Siren className="w-3 h-3" />
                                        STAT
                                    </span>
                                )}
                                {priority === 'urgent' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500 text-white text-[11px] font-black uppercase tracking-widest">
                                        URGENT
                                    </span>
                                )}
                                {order?.doctorName && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <Stethoscope className="w-3 h-3" />
                                        {order.doctorName}
                                    </span>
                                )}
                                {sample?.receivedAt && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <Clock className="w-3 h-3" />
                                        {t('labresultentry.received_at', 'Received {{when}}', { when: fmtDateTime(sample.receivedAt) })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="text-left lg:text-right">
                        {/* The order id is not drawn (owner, 29 Sep 2026: order ids are
                            hidden); the accession number on the left names the sample. */}
                        {sample?.sampleType && (
                            <div className="text-[11px] text-slate-500 mt-1">
                                {sample.sampleType}
                                {sample.status ? ` · ${sample.status}` : ''}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* A refresh that failed while values are already on screen. Said out
                loud rather than swallowed: what is displayed is now of unknown
                age, and a technician about to verify needs to know that. */}
            {error && (
                <div data-testid="lab-result-entry-stale" className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* ---- criticals already raised and not yet phoned ---- */}
            {unphoned.length > 0 && (
                <div data-testid="unphoned-alerts" className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border-2 border-red-300">
                    <Siren className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <div className="font-bold">
                            {t('labresultentry.unphoned_title', '{{n}} critical result(s) with nobody recorded as told', { n: unphoned.length })}
                        </div>
                        <ul className="mt-1 text-xs text-red-700 list-disc pl-4">
                            {unphoned.map((a) => (
                                <li key={a.alertId}>
                                    <strong>{a.testName}</strong>: {a.resultValue}
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-red-700 mt-1">
                            {t('labresultentry.unphoned_hint', 'A critical value has to reach a clinician by telephone. NABL wants who was told and when, so record it against the alert.')}
                        </p>
                    </div>
                </div>
            )}

            {/* ---- what this screen cannot flag for you ---- */}
            {!criticalPreviewAvailable && results.length > 0 && (
                <div data-testid="no-critical-preview" className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-xs">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                    {t(
                        'labresultentry.no_critical_preview',
                        'Panic limits are not loaded for these tests, so nothing here will turn red as you type. The server still applies them when you save — the colours on screen are a preview, never the decision.'
                    )}
                </div>
            )}

            {/* ---- the values ---- */}
            {results.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm border border-gray-200 rounded-xl">
                    {t('labresultentry.no_results', 'Nothing to enter — this order has no analytes on it yet.')}
                </div>
            ) : groups.map((group) => (
                <div key={group.key} className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-sm font-bold uppercase tracking-wider text-[#2D8A8A]">{group.sectionName}</h2>
                        {group.panelName && (
                            <>
                                <span className="text-slate-300">/</span>
                                <span
                                    title={t('labresultentry.panel_hint', 'A profile. It produces no result of its own — these are the analytes it contains.')}
                                    className="text-sm font-semibold text-slate-600"
                                >
                                    {group.panelName}
                                </span>
                            </>
                        )}
                    </div>

                    <div className="overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid={`results-table-${group.key}`} className="w-full border-collapse">
                            <thead>
                                <tr className="bg-slate-100 text-slate-600">
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider">{t('labresultentry.test', 'Test')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider w-44">{t('labresultentry.result', 'Result')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider w-32">{t('labresultentry.flag', 'Flag')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider w-48">{t('labresultentry.reference', 'Reference')}</th>
                                    <th className="p-2.5 text-left text-[11px] font-bold uppercase tracking-wider w-56">{t('labresultentry.trail', 'Trail')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.rows.map((result) => {
                                    const { flag, preview } = liveFlag(result);
                                    const dirty = dirtyIds.includes(result.resultId);
                                    const superseded = result.status === 'amended';
                                    return (
                                        <tr
                                            key={result.resultId}
                                            data-testid={`result-row-${result.resultId}`}
                                            className={`border-b border-gray-100 align-top ${isCritical(flag) ? 'bg-red-50/50' : ''} ${superseded ? 'opacity-60' : ''}`}
                                        >
                                            <td className="p-2.5">
                                                <div className={`text-sm font-semibold text-slate-700 ${superseded ? 'line-through' : ''}`}>
                                                    {result.testName}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                    {result.method && (
                                                        <span className="text-[10px] text-slate-400">{result.method}</span>
                                                    )}
                                                    {result.isOutsourced && (
                                                        <span
                                                            title={t('labresultentry.outsourced_hint', 'Referred out. The value comes from the partner lab, so it can be entered without a tube being received here.')}
                                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold uppercase"
                                                        >
                                                            <Building2 className="w-3 h-3" />
                                                            {result.partnerName || t('labresultentry.referred', 'Referred')}
                                                        </span>
                                                    )}
                                                    {superseded && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-bold uppercase">
                                                            <History className="w-3 h-3" />
                                                            {t('labresultentry.superseded', 'Superseded')}
                                                        </span>
                                                    )}
                                                </div>
                                                {result.amendReason && (
                                                    <div className="text-[10px] text-amber-700 mt-1">
                                                        {t('labresultentry.amend_reason_shown', 'Amended: {{why}}', { why: result.amendReason })}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="p-2.5">
                                                {renderInput(result)}
                                                {result.unit && EDITABLE_STATUSES.includes(result.status) && canEnter && (
                                                    <div className="text-[11px] text-slate-400 mt-1">{result.unit}</div>
                                                )}
                                                {dirty && (
                                                    <div data-testid={`unsaved-${result.resultId}`} className="text-[10px] text-amber-600 font-semibold mt-1">
                                                        {t('labresultentry.unsaved_row', 'not saved yet')}
                                                    </div>
                                                )}
                                                {result.status === 'released' && canEnter && (
                                                    <button
                                                        type="button"
                                                        data-testid={`amend-${result.resultId}`}
                                                        onClick={() => handleAmend(result)}
                                                        className="inline-flex items-center gap-1 mt-2 text-[11px] text-amber-700 font-semibold underline underline-offset-2 cursor-pointer"
                                                    >
                                                        <Pencil className="w-3 h-3" />
                                                        {t('labresultentry.amend', 'Amend')}
                                                    </button>
                                                )}
                                            </td>

                                            <td className="p-2.5">
                                                <FlagChip flag={flag} preview={preview} testId={`flag-${result.resultId}`} />
                                            </td>

                                            <td className="p-2.5">
                                                <ReferenceCell result={result} />
                                            </td>

                                            <td className="p-2.5">
                                                <ProvenanceCell result={result} />
                                                {canEnter && EDITABLE_STATUSES.includes(result.status) && (
                                                    <input
                                                        data-testid={`remarks-${result.resultId}`}
                                                        type="text"
                                                        value={remarksDraft[result.resultId] ?? ''}
                                                        maxLength={200}
                                                        onChange={(e) => setRemarksDraft((d) => ({ ...d, [result.resultId]: e.target.value.replace(/[<>]/g, '') }))}
                                                        placeholder={t('labresultentry.remarks_placeholder', 'remark (e.g. haemolysed, repeat)')}
                                                        className="mt-1.5 w-full p-1.5 border border-gray-200 rounded-lg text-[11px] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* ---- live criticals, before anything is saved ---- */}
            {liveCriticals.length > 0 && (
                <div data-testid="live-critical-banner" className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border-2 border-red-400">
                    <Siren className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <div className="font-bold">
                            {t('labresultentry.live_critical_title', 'A value you have typed is outside the panic limits')}
                        </div>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'labresultentry.live_critical_hint',
                                'Check it against the analyser before you save. If it is right, a clinician has to be telephoned — saving raises the alert that records who was told.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            {/* ================================================================
                THE WALL.

                Two panes, side by side, and everything about them is different:
                background, weight, wording, and the fact that only one of them
                opens a dialog. Entering a value and signing for it are not two
                sizes of the same action, and a bench under pressure reads that
                difference from the shape of the controls long before it reads
                the labels on them.
               ================================================================ */}
            <div
                data-testid="bench-action-bar"
                className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_20px_rgba(15,23,42,0.08)]"
            >
                <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-3 lg:items-stretch">
                    {/* ---- ENTER: quiet, frequent, reversible ---- */}
                    <div className="flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50">
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                                {t('labresultentry.enter_pane', 'Record values')}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 truncate">
                                {hasUnsaved
                                    ? t('labresultentry.unsaved_count', '{{n}} value(s) typed but not saved', { n: dirtyIds.length })
                                    : t('labresultentry.all_saved', 'Everything on screen is saved')}
                            </div>
                        </div>
                        {canEnter ? (
                            <button
                                type="button"
                                data-testid="save-results"
                                disabled={!hasUnsaved || saving}
                                onClick={handleSave}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm whitespace-nowrap ${(!hasUnsaved || saving) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-[#4ABDBD]/10'}`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {t('labresultentry.save', 'Save values')}
                            </button>
                        ) : (
                            <span
                                data-testid="cannot-enter"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-400 text-xs font-semibold whitespace-nowrap"
                            >
                                <Lock className="w-4 h-4" />
                                {t('labresultentry.cannot_enter', 'You may read, not enter')}
                            </span>
                        )}
                    </div>

                    {/* ---- VERIFY: rare, deliberate, and a signature ---- */}
                    <div
                        data-testid="verify-pane"
                        className={`flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 ${verifyBlockedBy ? 'border-slate-200 bg-slate-100' : 'border-slate-900 bg-slate-900'}`}
                    >
                        <div className="min-w-0">
                            <div className={`text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 ${verifyBlockedBy ? 'text-slate-500' : 'text-[#4ABDBD]'}`}>
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {t('labresultentry.verify_pane', 'Second-person verification')}
                            </div>

                            {/* Every closed state says what would open it, in the
                                order of who can do something about it. */}
                            {verifyBlockedBy === 'permission' && (
                                <div data-testid="verify-blocked-permission" className="text-xs text-slate-600 mt-0.5">
                                    {t(
                                        'labresultentry.verify_no_permission',
                                        'You can enter results but not approve them. A second person holding lab.result.verify has to sign these off before a report can be released.'
                                    )}
                                </div>
                            )}
                            {verifyBlockedBy === 'registration' && (
                                <div data-testid="verify-blocked-registration" className="text-xs text-amber-700 mt-0.5">
                                    {t(
                                        'labresultentry.verify_no_registration',
                                        'Your staff record has no registration number. A report carries the number of whoever signed it, so verification is not possible until an administrator adds one.'
                                    )}
                                </div>
                            )}
                            {verifyBlockedBy === 'unsaved' && (
                                <div data-testid="verify-blocked-unsaved" className="text-xs text-amber-700 mt-0.5">
                                    {t(
                                        'labresultentry.verify_unsaved',
                                        'Save first. Verification approves what is stored, not what is on screen.'
                                    )}
                                </div>
                            )}
                            {verifyBlockedBy === 'nothing' && (
                                <div data-testid="verify-blocked-nothing" className="text-xs text-slate-600 mt-0.5">
                                    {t('labresultentry.verify_nothing', 'Nothing is waiting to be verified.')}
                                </div>
                            )}
                            {!verifyBlockedBy && (
                                <div className="text-xs text-slate-300 mt-0.5">
                                    {t('labresultentry.verify_ready', '{{n}} result(s) ready to sign', { n: verifiableIds.length })}
                                    {viewer?.registrationNo && (
                                        <span className="text-slate-400">
                                            {` · ${viewer.name || ''} ${t('labresultentry.reg_no', 'Reg. no')} ${viewer.registrationNo}`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {verifyBlockedBy ? (
                            <span
                                data-testid="verify-locked"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-400 text-xs font-semibold whitespace-nowrap cursor-not-allowed"
                            >
                                <Lock className="w-4 h-4" />
                                {t('labresultentry.verify', 'Verify')}
                            </span>
                        ) : (
                            <button
                                type="button"
                                data-testid="verify-results"
                                disabled={verifying}
                                onClick={handleVerify}
                                className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#4ABDBD] text-slate-900 font-black text-sm uppercase tracking-wide whitespace-nowrap shadow-lg ${verifying ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#5fd0d0]'}`}
                            >
                                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
                                {t('labresultentry.verify_n', 'Verify {{n}}', { n: verifiableIds.length })}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
